// Customer address book — shared by Customer 360 and the Quote Builder.
// Existing person/order data is read-only; new addresses are saved separately.
import { useCallback, useEffect, useState } from "react";
import api from "./api";

export const ADDRESS_FIELDS = ["name", "company", "line1", "line2", "city", "county", "postcode", "country", "phone"];
export const emptyAddress = () => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, ""]));
export const hasAddress = (a) => !!(a && (a.line1 || a.postcode));
export const addressLines = (a) => (a ? [a.line1, a.line2, [a.city, a.county].filter(Boolean).join(", "), a.postcode, a.country].filter(Boolean) : []);
export const addressOneLine = (a) => addressLines(a).join(", ");
export const pickFields = (a) => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, (a && a[f]) || ""]));

export function useAddressBook(personId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!personId) { setData(null); return; }
    setLoading(true);
    try { setData((await api.get(`/persons/${personId}/addresses`)).data); }
    catch { setData({ billing: null, billing_options: [], delivery: [] }); }
    finally { setLoading(false); }
  }, [personId]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (kind, addr, extra = {}) => {
    const body = { ...pickFields(addr), label: addr.label || "", is_default: !!addr.is_default, kind, ...extra };
    const res = addr.saved && addr.id
      ? await api.patch(`/persons/${personId}/addresses/${addr.id}`, body)
      : await api.post(`/persons/${personId}/addresses`, body);
    await load();
    return res.data;
  }, [personId, load]);

  const remove = useCallback(async (id) => {
    await api.delete(`/persons/${personId}/addresses/${id}`);
    await load();
  }, [personId, load]);

  return { data, loading, reload: load, save, remove };
}
